/**
 * Marketplace constants and tree data structure for MFR forms
 */

export interface MarketplaceNode {
  title: string;
  value: string;
  key: string;
  children?: MarketplaceNode[];
}

// EU5 marketplace nodes
export const EU5_MARKETPLACES: MarketplaceNode[] = [
  { title: 'DE - Germany', value: 'DE', key: 'DE' },
  { title: 'ES - Spain', value: 'ES', key: 'ES' },
  { title: 'FR - France', value: 'FR', key: 'FR' },
  { title: 'IT - Italy', value: 'IT', key: 'IT' },
  { title: 'UK - United Kingdom', value: 'UK', key: 'UK' },
];

// Additional EU10 marketplaces (not in EU5)
export const EU10_ADDITIONAL_MARKETPLACES: MarketplaceNode[] = [
  { title: 'NL - Netherlands', value: 'NL', key: 'NL' },
  { title: 'PL - Poland', value: 'PL', key: 'PL' },
  { title: 'SE - Sweden', value: 'SE', key: 'SE' },
  { title: 'BE - Belgium', value: 'BE', key: 'BE' },
  { title: 'IE - Ireland', value: 'IE', key: 'IE' },
];

// EU5 group
export const EU5_GROUP: MarketplaceNode = {
  title: 'EU5',
  value: 'EU5',
  key: 'EU5',
  children: EU5_MARKETPLACES,
};

// EU10 group (contains EU5 nested)
export const EU10_GROUP: MarketplaceNode = {
  title: 'EU10',
  value: 'EU10',
  key: 'EU10',
  children: [EU5_GROUP, ...EU10_ADDITIONAL_MARKETPLACES],
};

// LATAM group
export const LATAM_GROUP: MarketplaceNode = {
  title: 'LATAM',
  value: 'LATAM',
  key: 'LATAM',
  children: [
    { title: 'BR - Brazil', value: 'BR', key: 'BR' },
    { title: 'MX - Mexico', value: 'MX', key: 'MX' },
  ],
};

// MENA group
export const MENA_GROUP: MarketplaceNode = {
  title: 'MENA',
  value: 'MENA',
  key: 'MENA',
  children: [
    { title: 'AE - United Arab Emirates', value: 'AE', key: 'AE' },
    { title: 'EG - Egypt', value: 'EG', key: 'EG' },
    { title: 'SA - Saudi Arabia', value: 'SA', key: 'SA' },
  ],
};

// NA Established marketplaces (US and CA)
export const NA_ESTABLISHED_MARKETPLACES: MarketplaceNode[] = [
  { title: 'US - United States', value: 'US', key: 'US' },
  { title: 'CA - Canada', value: 'CA', key: 'CA' },
];

// NA Established group (contains US and CA)
export const NA_ESTABLISHED_GROUP: MarketplaceNode = {
  title: 'NA Established',
  value: 'NA_ESTABLISHED',
  key: 'NA_ESTABLISHED',
  children: NA_ESTABLISHED_MARKETPLACES,
};

// Standalone marketplaces (without US and CA - they're in NA Established)
export const STANDALONE_MARKETPLACES: MarketplaceNode[] = [
  { title: 'AU - Australia', value: 'AU', key: 'AU' },
  { title: 'IN - India', value: 'IN', key: 'IN' },
  { title: 'JP - Japan', value: 'JP', key: 'JP' },
  { title: 'SG - Singapore', value: 'SG', key: 'SG' },
  { title: 'TR - Turkey', value: 'TR', key: 'TR' },
];

/**
 * Returns the complete marketplace tree data structure for MFR forms
 */
export const getMarketplaceTreeData = (): MarketplaceNode[] => {
  return [
    EU10_GROUP,
    LATAM_GROUP,
    MENA_GROUP,
    NA_ESTABLISHED_GROUP,
    ...STANDALONE_MARKETPLACES,
  ];
};
